import { CopyIcon, cn, DeleteIcon } from '@viapeople/components'
import { useCallback } from 'react'
import { useFormComponentWrapper } from '../../hooks/useFormElementWrapper'
import { useFormBuilderStore } from '../../store'
import { DeleteComponentDialog } from '../DeleteComponentDialog'
import { BaseFormComponentWrapper } from './BaseFormElementWrapper'
import type { FormComponentWrapperProps } from './types'

export const StandardFormComponentWrapper = ({ component, index, containerId }: FormComponentWrapperProps) => {
	const wrapperProps = useFormComponentWrapper(component, index, containerId)
	const {
		handleDelete,
		handleDuplicate,
		handleConfirmDelete,
		handleCancelDelete,
		isSelected,
		isDeleteDialogOpen,
		dependentComponents,
		componentLabel,
	} = wrapperProps
	const getComponentDefinition = useFormBuilderStore((state) => state.getComponentDefinition)
	const updateComponentProps = useFormBuilderStore((state) => state.updateComponentProps)

	const isDisplay = component.type === 'display'

	// Stable resize callback so the 9-box's pointer handlers aren't bound to a fresh function
	// each render. The 9-box clamps the new size against its parent column width before calling.
	const handleNineBoxResize = useCallback(
		(width: number, height: number) => {
			updateComponentProps(component.id, { cellSize: { width, height } })
		},
		[component.id, updateComponentProps],
	)

	return (
		<>
			<BaseFormComponentWrapper component={component} index={index} wrapperProps={wrapperProps}>
				<div className='flex'>
					<div
						className={cn(
							// `min-w-0` (scoped to 9-box) keeps the cells' min-content from forcing this
							// flex item wider than the section column, which would push the Delete/Duplicate
							// sibling buttons off-screen.
							'relative w-full border rounded-md cursor-pointer text-left flex',
							component.name === '9boxrating' && 'min-w-0',
							isDisplay ? 'p-2 bg-transparent items-center' : 'bg-neutral-0 p-6 items-center justify-start min-h-25',
							isSelected
								? 'border-primary-600'
								: isDisplay
									? 'border-dashed border-neutral-300 hover:border-neutral-400'
									: 'border-neutral-200 hover:border-neutral-300',
						)}>
						{(() => {
							const componentDef = getComponentDefinition(component.name)
							if (!componentDef) {
								return (
									<div className='flex items-center gap-2 text-neutral-500 italic py-2'>
										<span className='text-xs bg-neutral-100 px-2 py-1 rounded'>{component.name}</span>
										<span className='text-sm'>Component not available in form builder (view-only in client)</span>
									</div>
								)
							}
							const ComponentRenderer = componentDef.component
							if (component.name === '9boxrating') {
								return <ComponentRenderer component={component} editable={isSelected} onResize={handleNineBoxResize} />
							}
							// Admin builder canvas is the configuration surface, so unconfigured
							// renderers (e.g. external-link) should show their placeholder hint.
							return <ComponentRenderer component={component} isPreview />
						})()}
					</div>
					<div className='flex flex-col ml-2 gap-2'>
						<button
							type='button'
							className='py-2 px-4 bg-neutral-0 border border-neutral-200 rounded-md group group-hover:bg-neutral-50 cursor-pointer'
							onClick={handleDelete}
							title='Delete'>
							<DeleteIcon size={24} className='fill-neutral-500 group-hover:fill-error-600' />
						</button>
						<button
							type='button'
							className='py-2 px-4 bg-neutral-0 border border-neutral-200 rounded-md group group-hover:bg-neutral-50 cursor-pointer'
							onClick={handleDuplicate}
							title='Duplicate'>
							<CopyIcon size={24} className='fill-neutral-500 group-hover:fill-primary-600' />
						</button>
					</div>
				</div>
			</BaseFormComponentWrapper>
			<DeleteComponentDialog
				isOpen={isDeleteDialogOpen}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				componentLabel={componentLabel}
				dependentComponents={dependentComponents}
			/>
		</>
	)
}
